<script setup lang="ts">
import { useMagicKeys } from "@vueuse/core";

import type { BaseModalEmits, BaseModalProps } from "~/types/BaseModal";

const {
  backgroundClick = true,
  size = "sm",
  closeButton = true,
  position = "center",
  noPadding = false,
} = defineProps<BaseModalProps>();

const emit = defineEmits<BaseModalEmits>();

type Slots = {
  default(): HTMLElement;
  header?(): HTMLElement;
  footer?(): HTMLElement;
};

const slots = defineSlots<Slots>();

const hasHeaderAndFooter = computed(() => slots.header && slots.footer);

const platformStore = usePlatformStore();
const { getStyleFromTheme } = useGetStyleFromTheme();
const platformStyles = computed(() =>
  getStyleFromTheme(platformStore.getPlatformConfiguration.styling, {
    overwriteTheme: "light",
  }),
);

const close = () => emit("close");
const wrapperClose = () => {
  if (!backgroundClick) return;
  close();
};

// Lock/unlock body scroll on open/close modal
onBeforeMount(() => {
  disableBodyScroll();
});

onBeforeUnmount(() => {
  enableBodyScroll();
});

// Close modal and press escape
const { escape } = useMagicKeys();
watchEffect(() => {
  if (escape?.value) close();
});
</script>

<template>
  <Teleport to="#teleports">
    <Transition name="modal">
      <!-- we handle keyboard support with a composable -->
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
      <div
        v-bind="$attrs"
        :data-cy="dataCy"
        :data-test="dataTest"
        class="base-modal"
        :class="{
          'top-position': position === 'top',
          'bottom-position': position === 'bottom',
          'variant-header': Boolean(slots.header),
          'variant-actions': Boolean(slots.footer),
        }"
        :style="platformStyles"
        @click.self="wrapperClose()"
      >
        <div
          class="base-modal__container"
          :class="{
            [`size-${size}`]: true,
            'base-modal__container-background': Boolean(
              containerBackgroundTransparent,
            ),
          }"
        >
          <div class="base-modal__content">
            <button
              v-if="closeButton"
              class="cursor-pointer p-4 z-50 w-[72px] h-[72px] absolute top-0 right-0 flex items-center justify-center"
              data-cy="modal-close-button"
              @click="close()"
            >
              <BaseIcon
                name="close"
                :size="20"
              />
            </button>
            <div
              v-if="slots.header"
              class="base-modal__header uppercase"
            >
              <h2><slot name="header" /></h2>
            </div>
            <div
              class="base-modal__middle"
              :class="{
                'base-modal__solo-middle': !hasHeaderAndFooter,
                'no-padding': noPadding,
              }"
            >
              <slot />
            </div>
            <div
              v-if="slots.footer"
              class="base-modal__footer"
            >
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="postcss" scoped>
.base-modal {
  @apply fixed z-50 top-0 left-0 w-full h-screen;
  @apply flex justify-center items-center transition-opacity duration-300 ease-in-out;
  @apply bg-black bg-opacity-50;
  text-shadow: none;

  &.top-position {
    align-items: flex-start;
  }

  &.bottom-position {
    align-items: flex-end;
  }

  &.variant {
    &-actions {
      @apply p-0 md:p-2;
      .base-modal__container {
        @apply rounded-none border-none;
        @apply min-w-full min-h-full h-full md:h-fit md:min-w-0 md:min-h-0;
        @apply flex flex-col;

        &.size-sm {
          @apply w-80 min-h-0 min-w-0 h-auto;
        }

        .base-modal__header {
          @apply text-center md:text-left;
        }

        .base-modal__content {
          @apply md:max-h-[80vh];
          @apply flex-1 overflow-y-auto;
        }

        .base-modal__footer {
          @apply mt-auto md:pt-4;
          @apply border-t;
          @apply flex-col items-stretch text-center gap-4 md:flex-row md:items-center md:justify-end;

          > :deep(button) {
            @apply min-w-24;
          }
        }

        .base-modal__header {
          @apply md:text-left;
        }
      }
    }

    &-header {
      .base-modal__middle {
        @apply pt-0;
      }
    }
  }

  &__container {
    @apply relative my-0 mx-auto shadow-sm;
    @apply transition-all duration-300 ease-in-out;
    @apply max-w-full;
    &-background {
      @apply bg-transparent;
    }

    &.size {
      &-sm {
        @apply w-80;
      }

      &-md {
        @apply w-112;
      }

      &-lg {
        @apply w-150;
      }

      &-xl {
        @apply w-196;
      }

      &-full {
        @apply w-full h-screen p-0;
      }
    }
  }

  .position-from-top {
    /* this margin aligns with the header height */
    @apply mt-12 lg:mt-20;
  }

  &__content {
    @apply flex flex-col h-full;
  }

  &__header {
    @apply p-4;
    @apply text-lg font-bold;
  }

  &__footer {
    @apply border-t border-platform-border-1;
    @apply flex items-center justify-end p-4 gap-6;
  }

  &__middle {
    @apply overflow-y-auto p-4;

    &.no-padding {
      @apply px-0 pt-0 pb-0;
    }
  }

  &__solo-middle {
    @apply flex flex-col gap-y-1 h-full;
  }
}

/*
   * The following styles are auto-applied to elements with
   * transition="modal" when their visibility is toggled
   * by Vue.js.
   *
   * You can easily play with the modal transition by editing
   * these styles.
   */

.modal-enter {
  @apply opacity-0;
}

.modal-leave-active {
  @apply opacity-0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  @apply transform scale-110;
}
</style>
